import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = { label: "", lat: "", lng: "", state: "", note: "" };

function LocationForm({ onSubmitted }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [posting, setPosting] = useState(false);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setPosting(true);
    try {
      const { data } = await api.post("/location/update", {
        label: form.label,
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
        state: form.state,
        note: form.note,
      });
      const dropName = data?.drop?.name;
      toast(
        dropName
          ? `Pin dropped + new drop live: ${dropName}`
          : "Location pinned. Live feed updated."
      );
      setForm(EMPTY_FORM);
      onSubmitted?.();
    } catch (err) {
      console.warn("location update failed:", err?.response?.status || err?.message);
      toast("Failed to update location");
    } finally {
      setPosting(false);
    }
  };

  return (
    <form onSubmit={submit} className="tac-card p-7 space-y-4">
      <div className="overline">Drop new pin</div>
      <div className="text-xs text-white/60 -mt-1">
        <span className="text-[#FF1493]">⚡</span> Drops a 48-hour limited product tied to this
        city and fires a live banner.
      </div>
      <div>
        <label className="overline mb-1 block">City label</label>
        <input
          required
          value={form.label}
          onChange={(e) => setField("label", e.target.value)}
          placeholder="e.g. Sedona, AZ"
          className="tac-input"
          data-testid="loc-label"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="overline mb-1 block">Lat</label>
          <input
            required
            type="number"
            step="any"
            value={form.lat}
            onChange={(e) => setField("lat", e.target.value)}
            className="tac-input"
            data-testid="loc-lat"
          />
        </div>
        <div>
          <label className="overline mb-1 block">Lng</label>
          <input
            required
            type="number"
            step="any"
            value={form.lng}
            onChange={(e) => setField("lng", e.target.value)}
            className="tac-input"
            data-testid="loc-lng"
          />
        </div>
      </div>
      <div>
        <label className="overline mb-1 block">State (optional)</label>
        <input
          value={form.state}
          onChange={(e) => setField("state", e.target.value)}
          placeholder="AZ"
          className="tac-input"
          data-testid="loc-state"
        />
      </div>
      <div>
        <label className="overline mb-1 block">Note</label>
        <textarea
          rows={3}
          value={form.note}
          onChange={(e) => setField("note", e.target.value)}
          placeholder="What's happening here?"
          className="tac-input"
          data-testid="loc-note"
        />
      </div>
      <button type="submit" disabled={posting} className="btn-blaze w-full justify-center" data-testid="loc-submit">
        {posting ? "Pinning..." : "Drop pin · go live"}
      </button>
    </form>
  );
}

function RecentStops({ route }) {
  return (
    <div className="tac-card p-7">
      <div className="overline">Recent stops</div>
      <ul className="mt-4 divide-y divide-white/5">
        {route
          .slice()
          .reverse()
          .map((s) => (
            <li key={s.id} className="py-3 flex justify-between items-center" data-testid={`dash-stop-${s.id}`}>
              <div>
                <div className="font-semibold">{s.label}</div>
                <div className="font-mono-tactical text-xs text-white/50 mt-1">
                  {s.lat?.toFixed(3)}°, {s.lng?.toFixed(3)}° ·{" "}
                  {new Date(s.recorded_at).toLocaleDateString()}
                </div>
              </div>
              {s.current && <span className="overline">Live</span>}
            </li>
          ))}
        {route.length === 0 && (
          <li className="py-6 text-white/60 text-sm">No stops yet. Drop the first pin.</li>
        )}
      </ul>
    </div>
  );
}

export default function LocationControl() {
  const [route, setRoute] = useState([]);

  const loadRoute = useCallback(
    () => api.get("/location/route").then((r) => setRoute(r.data || [])),
    []
  );
  useEffect(() => {
    loadRoute();
  }, [loadRoute]);

  return (
    <div className="grid md:grid-cols-2 gap-8 mt-12">
      <LocationForm onSubmitted={loadRoute} />
      <RecentStops route={route} />
    </div>
  );
}
